import { useMemo } from 'react';
import styled from 'styled-components';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Span from '../../components/texts/span/Span';
import Table from '../../components/table/table';
import type { SgrDistributionResponseItem } from '../../types/interfaces';
import { useClientTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';
import { formatCurrency } from '../../utils/Masks';
import { CURRENCY_CODES } from '../../utils/Constants';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import {
  resolveSgrGrandDisplayTotals,
  resolveSgrGroupDisplayTotal,
} from '../../utils/sgrDistributionTotals';

export interface SgrApprovalDistributionReadOnlyProps {
  sgrDistributions: SgrDistributionResponseItem[];
}

const ResponsiveContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: 100%;
  }
`;

const SummaryContainer = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: 100%;
  }
`;

const SummaryCardWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  word-wrap: break-word;
  white-space: normal;
  gap: 8px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ResponsiveTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const GrandTotalRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 12px 24px;
  width: 100%;
`;

const GrandTotalLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
`;

const GrandTotalAmounts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

function SgrApprovalDistributionReadOnly({
  sgrDistributions,
}: SgrApprovalDistributionReadOnlyProps) {
  const { clientTheme } = useClientTheme();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());

  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { name: string; code: string; rows: SgrDistributionResponseItem[] }
    >();

    for (const row of sgrDistributions) {
      const key = row.sgrCode;
      if (!map.has(key)) {
        map.set(key, { name: row.sgrName, code: row.sgrCode, rows: [] });
      }
      map.get(key)!.rows.push(row);
    }
    return Array.from(map.values());
  }, [sgrDistributions]);

  const grandTotals = useMemo(
    () => resolveSgrGrandDisplayTotals(sgrDistributions, localCurrencyCode),
    [sgrDistributions, localCurrencyCode],
  );

  if (!sgrDistributions?.length) {
    return null;
  }

  const tableColumns = [
    {
      name: 'Producto',
      selector: (row: SgrDistributionResponseItem) => (
        <Span type='3' variant='neutral'>{row.productLineName}</Span>
      ),
      grow: 1,
    },
    {
      name: 'Moneda',
      selector: (row: SgrDistributionResponseItem) => (
        <Span type='3' variant='neutral'>{row.currencyCode}</Span>
      ),
      grow: 1,
    },
    {
      name: 'Monto',
      selector: (row: SgrDistributionResponseItem) => (
        <Span type='3' variant='neutral'>
          $ {formatCurrency(String(row.amount ?? 0), true)}
        </Span>
      ),
      grow: 1,
      right: true,
    },
  ];

  return (
    <Card margin={{ top: '2' }}>
      {grouped.map((g) => {
        const { ars: totalArs, usd: totalUsd } = resolveSgrGroupDisplayTotal(
          g.rows,
          localCurrencyCode,
        );

        return (
          <Card key={g.code} margin={{ top: '3' }}>
            <P type='2' variant='neutral' weight='bold' withMargin={false}>
              {g.name}
            </P>
            <Card margin={{ top: '2' }}>
              <ResponsiveContainer>
                <TableContainer>
                  <ResponsiveTableWrapper>
                    <Table
                      columns={tableColumns}
                      data={g.rows}
                      tableData={{ size: g.rows.length, totalElements: g.rows.length }}
                      variant='weak'
                    />
                  </ResponsiveTableWrapper>
                </TableContainer>
                <SummaryContainer>
                  <Card 
                    border={true} 
                    variant={clientTheme ? 'primary' : 'success'} 
                    modifier='weak' 
                    spacing='1'
                    width='100%'
                    height='100%'
                  >
                    <SummaryCardWrapper>
                      <ColumnContainer>
                        <P type='2' variant='neutral' withMargin={false}>
                          Total aprobado en ({g.name}):
                        </P>
                        <H
                          type='2'
                          withMargin={false}
                          weight='bold'
                          variant={clientTheme ? 'primary' : 'success'}
                          align='center'
                        >
                          {localCurrencyCode} {formatCurrency(String(totalArs))}
                        </H>
                        <H
                          type='2'
                          withMargin={false}
                          weight='bold'
                          variant={clientTheme ? 'primary' : 'success'}
                          align='center'
                        >
                          {CURRENCY_CODES.usd} {formatCurrency(String(totalUsd))}
                        </H>
                      </ColumnContainer>
                    </SummaryCardWrapper>
                  </Card>
                </SummaryContainer>
              </ResponsiveContainer>
            </Card>
          </Card>
        );
      })}

      <Card margin={{ top: '3' }}>
        <Card
          variant={clientTheme ? 'primary' : 'success'}
          modifier='weak'
          spacing='2'
          width='100%'
        >
          <GrandTotalRow>
            <GrandTotalLabel>
              <H type='3' variant='neutral-1' weight='bold' withMargin={false}>
                Total aprobado:
              </H>
              <P type='2' variant='neutral' modifier='weak' withMargin={false}>
                Suma de todas las SGR
              </P>
            </GrandTotalLabel>
            <GrandTotalAmounts>
              <H
                type='2'
                withMargin={false}
                weight='bold'
                variant={clientTheme ? 'primary' : 'success'}
              >
                {localCurrencyCode} {formatCurrency(String(grandTotals.ars))}
              </H>
              <H
                type='2'
                withMargin={false}
                weight='bold'
                variant={clientTheme ? 'primary' : 'success'}
              >
                {CURRENCY_CODES.usd} {formatCurrency(String(grandTotals.usd))}
              </H>
            </GrandTotalAmounts>
          </GrandTotalRow>
        </Card>
      </Card>
    </Card>
  );
}

export default SgrApprovalDistributionReadOnly;
